import { Button } from "@component/button";
import { useUserProfileContext } from "@context/user_profile";
import { trackEvent } from "@util/mixpanel";
import { useQuery } from "@util/useQuery";
import { ArrowRightIcon, CheckCircleIcon, LoaderIcon, UsersIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  title?: string
  message: string | React.ReactNode
  isDomainExist: boolean
  company_name?: string
  first_name?: string
  redirect_path?: string
  role: string
}

export default function ThankYou(props: Props) {
  const navigate = useNavigate();
  const queryParams = useQuery();
  const [isLoadingUserProfile, userInfo] = useUserProfileContext();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (props.isDomainExist) {
      setIsRedirecting(true);
      setTimeout(() => {
        navigate("/dashboard");
      }, 5000)
    }

    sessionStorage.removeItem("cv_uuid");
  }, [props.isDomainExist])


  const handleContinue = () => {
    setIsRedirecting(true);

    if (props.redirect_path === "") {
      trackEvent("candidate_registration_complete", {
        candidate_id: userInfo?.id,
        signup_method: userInfo?.is_connected_google ? "google" : "email"
      })
      setTimeout(() => {
        setIsRedirecting(false);
        window.location.href = "/dashboard";
      }, 2000);
    } else {
      trackEvent("continue_to_apply_clicked", {
        job_id: queryParams.get("ref") || "",
      })
      setTimeout(() => {
        setIsRedirecting(false);
        window.location.href = props.redirect_path || "/dashboard";
      }, 2000);
    }
  }

  return (
    <div className="max-w-4xl mx-auto flex flex-col items-center">
      {!props.isDomainExist ? (
        <>
          <div className="text-center">
            <div className="flex justify-center mb-4 md:mb-6">
              <CheckCircleIcon className="h-14 md:h-20 w-14 md:w-20 text-green-500" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 md:mb-6">{props.title ? props.title : "Thank you!"}</h1>
            <p className="text-sm md:text-xl text-gray-600 dark:text-gray-300">
              {props.message}
            </p>
            {props.role !== "hr" && (
              <Button
                variant="default"
                onClick={handleContinue}
                disabled={isRedirecting || isLoadingUserProfile}
                className="rounded-full mt-8"
              >
                {isRedirecting ? "Redirecting..." : props.redirect_path === "" ? "Go to Dashboard" : "Continue to Apply"}
              </Button>
            )}

          </div>
          {props.role === "hr" && (
            <div className="mt-4 w-full bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 py-8 md:py-16">
              <div className="flex flex-col items-center justify-center">
                <p className="text-sm md:text-lg text-gray-600 dark:text-gray-300 text-center">
                  Your onboarding is complete! <br />
                  <span className="text-blue-700">ourteam</span> will review
                  your application and contact you soon.
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
            <UsersIcon className="h-8 w-8 text-blue-600" />
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 text-center">
            You're officially joining {props.company_name}!
          </h2>
          <p className="mt-2 text-gray-600 max-w-md mx-auto text-center">
            Welcome aboard, <b>{props.first_name}</b>! Your account has been successfully
            verified and you now have access to your team's workspace at{" "}
            <b>{props.company_name}</b>.
          </p>

          <div className="mt-8">
            <Button
              className="bg-blue-600 hover:bg-blue-800 text-white"
              disabled={isRedirecting}
            >
              {isRedirecting ? (
                <div className="flex items-center">
                  <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting to Dashboard...
                </div>
              ) : (
                <>
                  Go to Dashboard
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
